// ─── Contexte « Mise en Pratique » (Core) — porté de MoPCal ───────
// Porte le REGISTRE des exercices (fourni par l'app : chaque module déclare ses
// pratiques, l'app compose) + l'état du RUNNER (exercice en cours, étapes
// cochées, réduit/déployé) + la PROGRESSION de l'utilisateur (users/{uid}).
//
//   <ApprentissageProvider pratiques={{ calendrier: [ {id,titre,etapes:[…]} ] }}>
// Une pratique : { id, titre, sousTitre?, emoji?, etapes:[{ consigne, indice? }] }
// Clé globale = `${fonction}.${id}` (suivi par utilisateur).
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { lireApprentissage, marquerMePFaite, marquerTutoVu } from './apprentissage';

const Ctx = createContext(null);
export const usePratique = () => useContext(Ctx) || {
  pratiques: {}, pratiquesDe: () => [], liste: [],
  actif: null, deploye: false, lancer: () => {}, cocher: () => {}, reduire: () => {}, agrandir: () => {}, fermer: () => {},
  apprentissage: { mep: {}, tutos: {} }, recharger: () => {}, terminerActif: async () => {}, marquerTuto: async () => {},
};

export function ApprentissageProvider({ pratiques = {}, children }) {
  const { user } = useAuth();

  // Registre à plat (fonction + clé globale sur chaque pratique).
  const liste = useMemo(() => Object.entries(pratiques)
    .flatMap(([f, arr]) => (arr || []).map(m => ({ ...m, fonction: f, cle: `${f}.${m.id}` }))), [pratiques]);
  const pratiquesDe = useCallback((fonction) =>
    liste.filter(m => m.fonction === fonction), [liste]);

  // Runner : { mep, faites: bool[] } + déployé/réduit.
  const [actif, setActif] = useState(null);
  const [deploye, setDeploye] = useState(true);
  const lancer = useCallback((mep) => { setActif({ mep, faites: (mep.etapes || []).map(() => false) }); setDeploye(true); }, []);
  const cocher = useCallback((i) => setActif(a => (a ? { ...a, faites: a.faites.map((v, k) => (k === i ? !v : v)) } : a)), []);
  const reduire = useCallback(() => setDeploye(false), []);
  const agrandir = useCallback(() => setDeploye(true), []);
  const fermer = useCallback(() => setActif(null), []);

  // Progression utilisateur (lecture au montage / changement de compte).
  const [apprentissage, setApprentissage] = useState({ mep: {}, tutos: {} });
  const recharger = useCallback(() => {
    lireApprentissage(user?.uid).then(setApprentissage).catch(() => {});
  }, [user?.uid]);
  useEffect(() => { recharger(); }, [recharger]);

  // Termine l'exercice ACTIF : écrit la progression + reflet local + ferme.
  const terminerActif = useCallback(async () => {
    const cle = actif?.mep?.cle;
    if (cle) {
      await marquerMePFaite(user?.uid, cle).catch(() => {});
      setApprentissage(a => ({ ...a, mep: { ...a.mep, [cle]: true } }));
    }
    setActif(null);
  }, [actif, user?.uid]);

  const marquerTuto = useCallback(async (cle) => {
    if (!cle) return;
    await marquerTutoVu(user?.uid, cle).catch(() => {});
    setApprentissage(a => ({ ...a, tutos: { ...a.tutos, [cle]: true } }));
  }, [user?.uid]);

  const val = useMemo(() => ({
    pratiques, pratiquesDe, liste,
    actif, deploye, lancer, cocher, reduire, agrandir, fermer,
    apprentissage, recharger, terminerActif, marquerTuto,
  }), [pratiques, pratiquesDe, liste, actif, deploye, lancer, cocher, reduire, agrandir, fermer, apprentissage, recharger, terminerActif, marquerTuto]);

  return <Ctx.Provider value={val}>{children}</Ctx.Provider>;
}
